import React, { createContext, useMemo, ReactNode, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { ReduxState } from '~/types/redux';
import { setChatbotStatus } from '~/redux/actions/chatbot';
import { showChatbot, hideChatbot, cleanupChatbot, updateKoreData } from './helpers';
import useChatbotMonarch, { UseChatbotMonarchReturnType } from './useChatbotMonarch';
import { useChatCallback } from './useChatCallback';
import { useChatTriggers, ChatTriggerType } from './useChatTriggers';
import { deriveViewState, getChatbotProps, getChatType, ChatbotViewState, ChatbotProps } from './calculations';
import { useChatThresholdTracking } from './useChatThresholdTracking';
import { useChatbotSDKInit } from './useChatbotSDKInit';
import { selectChatbotState } from '~/redux/selectors/chatbot';

export interface ChatbotContextValue {
  currentPage: string;
  firebase: unknown;
  monarchFlags: UseChatbotMonarchReturnType;

  viewState: ChatbotViewState;
  chatbotProps: ChatbotProps | null;
  chatType: 'GIGA' | 'PROACTIVE_V1' | 'PROACTIVE_V2' | 'DISABLED';

  showChat: (type: ChatTriggerType) => void;
  trackVasAction: () => boolean;
  trackProductSwitch: () => boolean;
}

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

const selectFirebase = (state: ReduxState): unknown => {
  return state?.firebase;
};

export const ChatbotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const currentPage = router?.pathname ?? '';
  const firebase = useSelector(selectFirebase);
  const monarchFlags = useChatbotMonarch();
  const chatbotState = useSelector(selectChatbotState);

  // Select Redux state slices for KORE data sync
  const { tracking, address, products, contact, sessionSummary } = useSelector((store: ReduxState) => store);

  const chatType = useMemo(() => getChatType(monarchFlags), [monarchFlags]);

  const viewState = useMemo(
    () => deriveViewState(monarchFlags, chatbotState, firebase, currentPage),
    [monarchFlags, chatbotState, firebase, currentPage]
  );

  const chatbotProps = useMemo(() => getChatbotProps(viewState), [viewState]);

  const triggersEnabled = useMemo(
    () => !monarchFlags.isLoading && chatType !== 'DISABLED',
    [monarchFlags.isLoading, chatType]
  );

  const { triggerChat } = useChatCallback({
    chatType,
    chatbotState,
    currentPage,
    monarchFlags,
  });

  const { trackVasAction, trackProductSwitch } = useChatThresholdTracking({
    vasThreshold: monarchFlags.triggers?.vasUncertainty?.threshold,
    productThreshold: monarchFlags.triggers?.productUncertainty?.threshold,
    currentPage,
  });

  const showChat = useChatTriggers({
    enabled: triggersEnabled,
    triggerCallback: triggerChat,
    idleTimeout: monarchFlags.triggers?.idle?.timeout,
    backButtonEnabled: monarchFlags.triggers?.backButton?.enabled,
  });

  // Initialize KORE SDK with editable text values from Monarch
  useChatbotSDKInit({
    monarchFlags,
  });

  // Sync Redux state (tracking, address, products, contact, sessionSummary) to KORE data
  useEffect(() => {
    updateKoreData(tracking, address, products, contact, sessionSummary);
  }, [tracking, address, products, contact, sessionSummary]);

  // Sync chatbot type (GIGA/PROACTIVE/DISABLED) from monarch flags to Redux status
  useEffect(() => {
    if (monarchFlags.isLoading) {
      hideChatbot();
      return;
    }

    if (chatType === 'DISABLED') {
      if (chatbotState.status !== 'DISABLED') {
        dispatch(setChatbotStatus('DISABLED'));
      }
      return;
    }

    if (chatType === 'GIGA' && chatbotState.status !== 'GIGA_CHAT_VISIBLE') {
      dispatch(setChatbotStatus('GIGA_CHAT_VISIBLE'));
    } else if (
      (chatType === 'PROACTIVE_V1' || chatType === 'PROACTIVE_V2') &&
      chatbotState.status !== 'PROACTIVE_CHAT_VISIBLE'
    ) {
      dispatch(setChatbotStatus('PROACTIVE_CHAT_VISIBLE'));
    }
  }, [chatType, monarchFlags.isLoading, chatbotState.status, dispatch]);

  // Sync Redux chatbot status to DOM visibility (show/hide chatbot UI)
  useEffect(() => {
    if (monarchFlags.isLoading || chatbotState.status === 'HIDDEN' || chatbotState.status === 'DISABLED') {
      hideChatbot();
    } else if (viewState.type !== 'DISABLED' && viewState.type !== 'LOADING') {
      showChatbot();
    }
  }, [chatbotState.status, monarchFlags.isLoading, viewState.type]);

  // Cleanup chatbot when fully disabled (remove DOM elements and SDK references)
  useEffect(() => {
    if (!monarchFlags.isLoading && chatbotState.status === 'DISABLED' && chatType === 'DISABLED') {
      const cleanupTimer = setTimeout(() => {
        cleanupChatbot();
      }, 500);

      return () => clearTimeout(cleanupTimer);
    }
  }, [chatbotState.status, monarchFlags.isLoading, chatType]);

  // Hide chatbot on component unmount
  useEffect(() => {
    return () => {
      hideChatbot();
    };
  }, []);

  const contextValue: ChatbotContextValue = useMemo(
    () => ({
      currentPage,
      firebase,
      monarchFlags,
      viewState,
      chatbotProps,
      chatType,
      showChat,
      trackVasAction,
      trackProductSwitch,
    }),
    [
      currentPage,
      firebase,
      monarchFlags,
      viewState,
      chatbotProps,
      chatType,
      showChat,
      trackVasAction,
      trackProductSwitch,
    ]
  );

  return <ChatbotContext.Provider value={contextValue}>{children}</ChatbotContext.Provider>;
};

ChatbotProvider.displayName = 'ChatbotProvider';

export default ChatbotContext;
